
'use client';

import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { db, storage } from '@/lib/firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';
import type { ThemeLogoSettings } from '@/types';

interface BrandingSettings {
    appName: string;
    globalLogoUrl: string;
}

interface SettingsContextType {
  settings: ThemeLogoSettings | null;
  appName: string;
  globalLogoUrl: string;
  loading: boolean;
  updateLogo: (files: Record<string, File>) => Promise<void>;
  updateBranding: (data: { appName?: string; logoFile?: File }) => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const defaultThemeSettings: ThemeLogoSettings = {
    default: '/logos/logo-default.svg',
    forest: '/logos/logo-forest.svg',
    rose: '/logos/logo-rose.svg',
    ocean: '/logos/logo-ocean.svg',
    sunset: '/logos/logo-sunset.svg',
};

const defaultBrandingSettings: BrandingSettings = {
    appName: 'Facefolio',
    globalLogoUrl: '/logos/logo-ocean.svg',
}

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState<ThemeLogoSettings | null>(null);
  const [branding, setBranding] = useState<BrandingSettings>(defaultBrandingSettings);
  const [loading, setLoading] = useState(true);
  const { user, isOwner } = useAuth();

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    const themeSettingsRef = doc(db, 'settings', 'theme-logos');
    const brandingRef = doc(db, 'settings', 'branding');
    
    try {
        const themeDocSnap = await getDoc(themeSettingsRef);
        if (themeDocSnap.exists()) {
            setSettings(themeDocSnap.data() as ThemeLogoSettings);
        } else {
            if (isOwner) await setDoc(themeSettingsRef, defaultThemeSettings);
            setSettings(defaultThemeSettings);
        }

        const brandingDocSnap = await getDoc(brandingRef);
        if (brandingDocSnap.exists()) {
            setBranding(brandingDocSnap.data() as BrandingSettings);
        } else {
            if (isOwner) await setDoc(brandingRef, defaultBrandingSettings);
            setBranding(defaultBrandingSettings);
        }
    } catch (error) {
        console.error("Error fetching settings, using defaults.", error);
        setSettings(defaultThemeSettings);
        setBranding(defaultBrandingSettings);
    } finally {
        setLoading(false);
    }
  }, [isOwner]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateLogo = async (files: Record<string, File>) => {
    if (!user || !isOwner) throw new Error("Not authorized");
    
    const settingsRef = doc(db, 'settings', 'theme-logos');
    const updates: Partial<ThemeLogoSettings> = {};

    for (const themeId in files) {
      if (Object.prototype.hasOwnProperty.call(files, themeId)) {
        const file = files[themeId];
        const filePath = `logos/${themeId}-${uuidv4()}`;
        const storageRef = ref(storage, filePath);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);
        updates[themeId as keyof ThemeLogoSettings] = downloadUrl;
      }
    }

    await updateDoc(settingsRef, updates);
    fetchSettings(); // Refresh local state
  };

  const updateBranding = async (data: { appName?: string; logoFile?: File }) => {
     if (!user || !isOwner) throw new Error("Not authorized");

     const brandingRef = doc(db, 'settings', 'branding');
     const updates: Partial<BrandingSettings> = {};

     if (data.appName) {
         updates.appName = data.appName;
     }

     if (data.logoFile) {
        const filePath = `logos/global-${uuidv4()}`;
        const storageRef = ref(storage, filePath);
        await uploadBytes(storageRef, data.logoFile);
        updates.globalLogoUrl = await getDownloadURL(storageRef);
     }

     if (Object.keys(updates).length > 0) {
        await updateDoc(brandingRef, updates);
        fetchSettings(); // Refresh local state
     }
  };


  return (
    <SettingsContext.Provider value={{ settings, appName: branding.appName, globalLogoUrl: branding.globalLogoUrl, loading, updateLogo, updateBranding }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
